<template>
  <div>
    <transition mode="out-in" name="fade" v-if="!loaded">
      <Loader />
    </transition>
    <transition
      mode="out-in"
      name="fade"
      v-else-if="canDisplay"
      v-visible="displayContent"
    >
      <App />
    </transition>
    <DebugApp :debugURL="debugURL" />
  </div>
</template>

<script lang="ts">
import DebugApp from "@fresh8/debug-kit/src/App.vue";
import { defineComponent, ref, watchEffect, computed, Ref } from "vue";
import { setupContext, DebugContext } from "@fresh8/debug-kit/src/debugContext";
import { adtag, getPrototype } from "./prefetch";
import Properties from "./properties";
import {
  provideBetData,
  provideBetDataRef
} from "@fresh8/ad-data-composition/src/betData";
import {
  provideBrand,
  provideAdTag
} from "@fresh8/ad-data-composition/src/brand";

import { provideDisplayEvents } from "./composition/displayEvents";
import { useGetBetData } from "./hooks/useGetBetData";

import { awaitPromise } from "./lib/awaitPromise";

import { BetData } from "@fresh8/types/lib/betData";
import { AdTag, PotentialWinnings } from "@fresh8/types/src/index";
import { provideTheme } from "@fresh8/ad-data-composition/src/theme";
import { providePreview } from "@fresh8/ad-data-composition/src/preview";
import { providePreroll } from "./hooks/usePreroll";

import { useLocalstorageRef } from "@fresh8/vue-utils/src/useLocalStorageRef";

// simple wrapper to enable HMR
import App from "./App.vue";
import Loader from "./components/Loader.vue";
import { createJssContext, provideJssContext } from "@fresh8/vue-jss/src";
import {
  provideAnalyticsContext,
  createAnalyticsContext
} from "./lib/analytics";
import { Preview } from "@fresh8/types/src/preview";
import { provideFeatureFlag } from "./hooks/useFeatureFlag";

function useDebug({
  adTag,
  adTagLoading,
  betData,
  betDataLoading,
  debugContext
}: {
  adTag: Ref<AdTag>;
  adTagLoading: Ref<boolean>;
  betData: Ref<BetData>;
  betDataLoading: Ref<boolean>;
  debugContext: DebugContext;
}) {
  const viewIDRef = ref();
  let localStorageDataRef;

  watchEffect(() => {
    if (
      viewIDRef.value &&
      debugContext.enableCapture.value &&
      !localStorageDataRef
    ) {
      localStorageDataRef = useLocalstorageRef(
        `captured-id-${viewIDRef.value}`,
        Object,
        {} // shouldn't be used as it should already be set by the watch effect below
      );
    }

    if (localStorageDataRef && localStorageDataRef.value) {
      adTag.value = localStorageDataRef.value.adTagData;
      betData.value = localStorageDataRef.value.betData;
    }
  });

  watchEffect(() => {
    if (adTagLoading.value === false && betDataLoading.value === false) {
      if (debugContext.enableCapture.value) {
        const viewID = betData.value.analytics.viewId;
        viewIDRef.value = viewID;
        debugContext.setEvent(viewID, {
          viewID,
          adTagData: adTag.value,
          betData: betData.value,
          time: Date.now().toString()
        });
      }
    }
  });
}

export default defineComponent({
  name: "AppWrapper",
  components: {
    App,
    Loader,
    DebugApp
  },
  setup() {
    const displayContent = ref(false);
    const prototype = getPrototype();
    const betDataContext = {
      betData: ref<BetData>(),
      get: useGetBetData,
      loading: ref(false)
    };

    const {
      result: adTag,
      loading: adTagLoading,
      error: adTagError
    } = awaitPromise<AdTag>(adtag);

    const {
      result: betResult,
      loading: betDataLoading,
      error: betDataError
    } = useGetBetData();

    watchEffect(() => {
      if (betResult.value) {
        betDataContext.betData.value = betResult.value;

        if (adTag.value && betResult.value.operator?.name) {
          adTag.value.operator = {
            key: betResult.value.operator.name,
            name: betResult.value.operator.friendlyName,
            _id: betResult.value.operator._id
          };
        }

        setTimeout(() => {
          displayContent.value = true;
        }, 1000);
      }
    });

    watchEffect(() => {
      if (prototype && adTag.value) {
        if (prototype === "potentialWinnings") {
          adTag.value.brand.adTemplate.potentialWinnings = {
            stakeFont: "",
            stakeAmountFont: ""
          } as PotentialWinnings;
          adTag.value.brand.adTemplate.market.oddsButtonTemplate =
            "potentialWinnings";
        }
      }
    });

    const jssContext = createJssContext({});

    const debugContext = setupContext();
    const brand = computed(() => adTag.value?.brand);
    const preview = computed(() => ({ isPreview: false } as Preview));

    provideBetDataRef(betDataContext);
    provideFeatureFlag();
    provideJssContext(jssContext);
    provideBetData(betDataContext.betData);
    provideAdTag(adTag);
    provideBrand(brand);
    providePreview(preview);

    // setup prerolls
    const sizeId = computed(() => adTag.value.size._id);
    const prerollImageSets = computed(() => brand.value?.prerollImageSets);
    const brandPreroll = computed(() => brand.value?.adTemplate.preroll);
    providePreroll(brandPreroll, sizeId, prerollImageSets);

    const analyticsContext = createAnalyticsContext(
      betDataContext.betData,
      adTag,
      debugContext.enableAnalytics
    );

    provideAnalyticsContext(analyticsContext);
    provideDisplayEvents(betDataContext.betData);
    provideTheme(ref({}));

    watchEffect(() => {
      if (adTagError.value) {
        window.parent.postMessage({ type: "f8_failed_loading" }, "*");
        document.documentElement.style.height = "0px";
        document.documentElement.style.overflow = "hidden";
        document.body.style.height = "0px";
        document.body.style.overflow = "hidden";
      }
    });

    useDebug({
      adTag,
      adTagLoading,
      betData: betDataContext.betData,
      betDataLoading,
      debugContext
    });

    const loaded = computed(
      () => !adTagLoading.value && !betDataLoading.value && !adTagError.value
    );

    watchEffect(() => {
      if (betDataError.value && !adTagError.value) {
        displayContent.value = true;
      }
    });

    return {
      loaded,
      canDisplay: computed(() => adTag.value.brand),
      displayContent,
      debugURL: Properties.debugUri
    };
  }
});
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
